import React, { useState } from 'react';
import { Search, ChevronDown, History, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useRecentSearches } from './useRecentSearches';

const SearchHeader = ({ moduleKey, schema, searchValues, onSearch }) => {
  const Icon = schema.icon;
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(searchValues);
  const { items: recent, add: addRecent, clear: clearRecent } = useRecentSearches(moduleKey);

  React.useEffect(() => { setDraft(searchValues); }, [searchValues]);

  const handleField = (name, value, field) => {
    let v = value;
    if (field.uppercase) v = String(v).toUpperCase();
    if (field.maxLength) v = String(v).slice(0, field.maxLength);
    setDraft((d) => ({ ...d, [name]: v }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    addRecent(draft);
    onSearch(draft);
    setExpanded(false);
  };

  // Pretty summary for the collapsed sticky bar
  const summary = schema.fields.slice(0, 4).map((f) => {
    const v = searchValues[f.name];
    if (!v) return null;
    return { label: f.label, value: v };
  }).filter(Boolean);

  return (
    <div className={`bg-gradient-to-r ${schema.headerGradient} text-white shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {/* Collapsed sticky bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="bg-white/20 p-2 rounded-lg">
              <Icon className="h-5 w-5" />
            </div>
            <div className="hidden sm:block">
              <p className="text-xs uppercase tracking-wider opacity-80">{schema.label}</p>
              <p className="text-sm font-semibold">Search Results</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3 flex-1 min-w-0">
            {summary.map((s) => (
              <div key={s.label} className="bg-white/10 hover:bg-white/15 rounded-md px-3 py-1.5 transition-colors">
                <p className="text-[10px] uppercase tracking-wider opacity-70 leading-none">{s.label}</p>
                <p className="text-sm font-semibold leading-tight truncate max-w-[140px]">{s.value}</p>
              </div>
            ))}
          </div>

          <Button
            onClick={() => setExpanded((e) => !e)}
            data-testid="modify-search-btn"
            className="bg-white text-red-700 hover:bg-red-50 font-semibold shadow-sm"
          >
            {expanded ? <X className="h-4 w-4 mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}
            {expanded ? 'Close' : 'Modify Search'}
            {!expanded && <ChevronDown className="h-4 w-4 ml-1 opacity-70" />}
          </Button>
        </div>

        {/* Expanded form */}
        {expanded && (
          <form
            onSubmit={handleSubmit}
            data-testid="modify-search-form"
            className="mt-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl shadow-2xl p-4 sm:p-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {schema.fields.map((f) => (
                <div key={f.name}>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{f.label}</Label>
                  {f.type === 'select' ? (
                    <select
                      value={draft[f.name] || ''}
                      onChange={(e) => handleField(f.name, e.target.value, f)}
                      data-testid={`field-${f.name}`}
                      className="mt-1 w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm"
                    >
                      {(f.options || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                      min={f.min}
                      max={f.max}
                      placeholder={f.placeholder}
                      value={draft[f.name] ?? ''}
                      onChange={(e) => handleField(f.name, e.target.value, f)}
                      data-testid={`field-${f.name}`}
                      className={`mt-1 ${f.type === 'date' ? '[color-scheme:light] text-gray-900' : ''}`}
                    />
                  )}
                </div>
              ))}
            </div>

            {recent.length > 0 && (
              <div className="mt-4 flex items-start gap-3 flex-wrap text-sm">
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 flex-shrink-0">
                  <History className="h-3.5 w-3.5" />
                  <span>Recent:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setDraft(r)}
                      data-testid={`recent-search-${i}`}
                      className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-300 text-xs transition-colors"
                    >
                      {schema.fields.slice(0, 2).map((f) => r[f.name]).filter(Boolean).join(' → ')}
                    </button>
                  ))}
                  <button type="button" onClick={clearRecent} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <Button type="submit" data-testid="search-submit-btn" className="bg-red-600 hover:bg-red-700 text-white px-8">
                <Search className="h-4 w-4 mr-1.5" />
                Search
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SearchHeader;
